(function () {
	'use strict';

	const tagName = 'syk-countdown';

	if (!('customElements' in window) || window.customElements.get(tagName)) {
		return;
	}

	class SykCountdown extends HTMLElement {
		constructor() {
			super();
			this.timerId = null;
			this.connectionId = 0;
			this.config = null;
			this.sections = null;
			this.enhanced = false;
			this.ended = false;
		}

		connectedCallback() {
			this.connectionId += 1;
			this.clearTimer();

			if (this.ended) {
				return;
			}

			this.config = this.readConfig();

			if (!this.config) {
				this.revealFallback();
				return;
			}

			const now = Date.now();

			if (!this.isActive(now)) {
				this.finish();
				return;
			}

			this.enhance();
			this.tick(this.connectionId);
		}

		disconnectedCallback() {
			this.connectionId += 1;
			this.clearTimer();
		}

		readConfig() {
			const startAtValue = this.getAttribute('data-start-at');
			const endAtValue = this.getAttribute('data-end-at');
			const activeDays = this.getAttribute('data-active-days');
			const endBehavior = this.getAttribute('data-end-behavior');
			const startAt = this.parseTimestamp(startAtValue);
			const endAt = this.parseTimestamp(endAtValue);

			if (
				startAt === null ||
				endAt === null ||
				startAt > endAt ||
				typeof activeDays !== 'string' ||
				!/^[0-6]*$/.test(activeDays) ||
				(endBehavior !== 'standalone' && endBehavior !== 'shipping')
			) {
				return null;
			}

			return {
				startAt: startAt,
				endAt: endAt,
				activeDays: activeDays,
				endBehavior: endBehavior,
				words: {
					day: this.getAttribute('data-day') || 'day',
					days: this.getAttribute('data-days') || 'days',
					hour: this.getAttribute('data-hour') || 'hour',
					hours: this.getAttribute('data-hours') || 'hours',
					minute: this.getAttribute('data-minute') || 'minute',
					minutes: this.getAttribute('data-minutes') || 'minutes',
					second: this.getAttribute('data-second') || 'second',
					seconds: this.getAttribute('data-seconds') || 'seconds'
				}
			};
		}

		parseTimestamp(value) {
			if (value === null || !/^\d+$/.test(value.trim())) {
				return null;
			}

			const seconds = Number(value);

			if (!Number.isFinite(seconds) || !Number.isInteger(seconds) || seconds > Math.floor(Number.MAX_SAFE_INTEGER / 1000)) {
				return null;
			}

			return seconds * 1000;
		}

		isActive(now) {
			if (now < this.config.startAt || now >= this.config.endAt) {
				return false;
			}

			return this.config.activeDays.includes(String(new Date(now).getUTCDay()));
		}

		enhance() {
			if (this.enhanced) {
				return;
			}

			const fallback = document.createElement('span');
			fallback.className = 'hidden';
			fallback.setAttribute('data-countdown-fallback', '');

			while (this.firstChild) {
				fallback.appendChild(this.firstChild);
			}

			this.appendChild(fallback);
			this.sections = {
				days: this.createSection('days'),
				hours: this.createSection('hours'),
				minutes: this.createSection('minutes'),
				seconds: this.createSection('seconds')
			};
			this.enhanced = true;
		}

		createSection(unit) {
			const section = document.createElement('div');
			const inner = document.createElement('div');
			const amount = document.createElement('span');
			const word = document.createElement('span');

			section.className = 'simply-section simply-' + unit + '-section';
			amount.className = 'simply-amount';
			word.className = 'simply-word';
			inner.appendChild(amount);
			inner.appendChild(word);
			section.appendChild(inner);
			this.appendChild(section);

			return {
				amount: amount,
				word: word
			};
		}

		tick(connectionId) {
			if (!this.isConnected || connectionId !== this.connectionId || this.ended) {
				return;
			}

			this.clearTimer();

			const remaining = this.config.endAt - Date.now();

			if (remaining <= 0) {
				this.finish();
				return;
			}

			const totalSeconds = Math.ceil(remaining / 1000);
			const days = Math.floor(totalSeconds / 86400);
			const hours = Math.floor((totalSeconds % 86400) / 3600);
			const minutes = Math.floor((totalSeconds % 3600) / 60);
			const seconds = totalSeconds % 60;

			this.renderUnit('days', days, this.config.words.day, this.config.words.days);
			this.renderUnit('hours', hours, this.config.words.hour, this.config.words.hours);
			this.renderUnit('minutes', minutes, this.config.words.minute, this.config.words.minutes);
			this.renderUnit('seconds', seconds, this.config.words.second, this.config.words.seconds);
			this.markReady();

			const remainder = remaining % 1000;
			const delay = remainder === 0 ? 1000 : Math.max(50, Math.min(1000, remainder));
			this.timerId = window.setTimeout(() => {
				if (connectionId !== this.connectionId) {
					return;
				}

				this.timerId = null;
				this.tick(connectionId);
			}, delay);
		}

		renderUnit(unit, value, singular, plural) {
			const section = this.sections[unit];
			const amount = String(value).padStart(2, '0');

			while (section.amount.firstChild) {
				section.amount.removeChild(section.amount.firstChild);
			}

			for (const digit of amount) {
				const digitElement = document.createElement('span');
				digitElement.textContent = digit;
				section.amount.appendChild(digitElement);
			}

			section.word.textContent = value > 1 ? plural : singular;
		}

		markReady() {
			this.classList.add('done');
			const owner = this.findOwner(this.getAttribute('data-end-behavior'));

			if (owner) {
				owner.classList.add('done');
			}
		}

		revealFallback() {
			this.restoreFallback();
			this.classList.add('done');
			const behavior = this.getAttribute('data-end-behavior');
			const owner = this.findOwner(behavior);

			if (owner) {
				owner.classList.add('done');
			}
		}

		restoreFallback() {
			const fallback = this.querySelector('[data-countdown-fallback]');

			if (!fallback) {
				return;
			}

			const fragment = document.createDocumentFragment();

			while (fallback.firstChild) {
				fragment.appendChild(fallback.firstChild);
			}

			while (this.firstChild) {
				this.removeChild(this.firstChild);
			}

			this.appendChild(fragment);
			this.sections = null;
			this.enhanced = false;
		}

		finish() {
			if (this.ended) {
				return;
			}

			this.ended = true;
			this.clearTimer();

			const behavior = this.config ? this.config.endBehavior : this.getAttribute('data-end-behavior');
			const owner = this.findOwner(behavior);

			if (!owner) {
				this.revealFallback();
				return;
			}

			if (behavior === 'shipping') {
				owner.remove();
				return;
			}

			const endedShow = owner.querySelector('.countdown-ended-show');
			const endedHide = owner.querySelector('.countdown-ended-hide');

			if (endedShow && endedHide) {
				this.remove();
				endedHide.remove();
				endedShow.classList.remove('hidden');
				return;
			}

			owner.remove();
		}

		findOwner(behavior) {
			let owner = this.parentElement;

			while (owner) {
				if (owner.getAttribute('data-countdown-owner') === behavior) {
					return owner;
				}

				owner = owner.parentElement;
			}

			return null;
		}

		clearTimer() {
			if (this.timerId !== null) {
				window.clearTimeout(this.timerId);
				this.timerId = null;
			}
		}
	}

	window.customElements.define(tagName, SykCountdown);
})();
